import { Pencil, Trash2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import type { ScheduledReportDto } from '@/features/reports/api/reports-api';
import { REPORT_TYPE_LABELS, FREQUENCY_LABELS } from './scheduled-report-labels';

function formatNullableDate(value?: string): string {
  return value ? formatDate(value) : '—';
}

interface ScheduledReportRowProps {
  schedule: ScheduledReportDto;
  canRunNow: boolean;
  // Per-row mutation flag from the parent so disable + spinner stays scoped
  // to the row whose action was clicked.
  isBusy: boolean;
  // Inline status message rendered in the row (e.g. "Triggered — report
  // will be emailed shortly"). Lives on the parent so other actions on the
  // same row can clear or replace it.
  statusMessage?: string;
  statusMessageVariant?: 'success' | 'error';
  onEdit: (schedule: ScheduledReportDto) => void;
  onCancel: (schedule: ScheduledReportDto) => void;
  onRunNow: (schedule: ScheduledReportDto) => void;
}

export function ScheduledReportRow({
  schedule,
  canRunNow,
  isBusy,
  statusMessage,
  statusMessageVariant = 'success',
  onEdit,
  onCancel,
  onRunNow,
}: ScheduledReportRowProps) {
  const reportLabel = REPORT_TYPE_LABELS[schedule.reportType] ?? schedule.reportType;
  const frequencyLabel = FREQUENCY_LABELS[schedule.frequency] ?? schedule.frequency;

  return (
    <TableRow data-testid={`scheduled-report-row-${schedule.id}`}>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">{reportLabel}</span>
          <Badge variant="default" size="sm">{schedule.format.toUpperCase()}</Badge>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-sm text-gray-700 dark:text-gray-300">{frequencyLabel}</span>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-sm text-gray-700 dark:text-gray-300 break-all">
            {schedule.deliveryEmail}
          </span>
          {/* Secondary subject line. Custom values render plain; the default
              renders muted+italic so it's visually distinct from a real
              override at a glance. Truncated with title= for the full text on
              hover, since long custom subjects are likely. */}
          {schedule.subject && schedule.subject.trim().length > 0 ? (
            <span
              className="text-xs text-gray-500 dark:text-gray-400 truncate"
              title={schedule.subject}
              data-testid={`scheduled-report-subject-${schedule.id}`}
            >
              {schedule.subject}
            </span>
          ) : (
            <span
              className="text-xs italic text-gray-400 dark:text-gray-500"
              data-testid={`scheduled-report-subject-${schedule.id}`}
            >
              Default subject
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
          <div>
            <span className="text-gray-400 dark:text-gray-500">Next:</span>{' '}
            <span className="text-gray-800 dark:text-gray-200">{formatNullableDate(schedule.nextRunAt)}</span>
          </div>
          <div>
            <span className="text-gray-400 dark:text-gray-500">Last:</span>{' '}
            <span className="text-gray-800 dark:text-gray-200">{formatNullableDate(schedule.lastRunAt)}</span>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={schedule.isActive ? 'success' : 'secondary'} size="sm" dot>
          {schedule.isActive ? 'Active' : 'Paused'}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(schedule)}
              disabled={isBusy}
              data-testid={`scheduled-report-edit-${schedule.id}`}
              title="Edit schedule"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCancel(schedule)}
              disabled={isBusy}
              data-testid={`scheduled-report-cancel-${schedule.id}`}
              title="Cancel schedule"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Cancel
            </Button>
            {canRunNow && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRunNow(schedule)}
                isLoading={isBusy}
                data-testid={`scheduled-report-run-${schedule.id}`}
                title="Run this report now"
              >
                {!isBusy && <Play className="w-3.5 h-3.5" />}
                Run now
              </Button>
            )}
          </div>
          {statusMessage && (
            <p
              className={
                statusMessageVariant === 'error'
                  ? 'text-xs text-error-600'
                  : 'text-xs text-success-600'
              }
              role="status"
            >
              {statusMessage}
            </p>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
